<template>
  <div
    ref="avatarRoot"
    :class="[
      'circle-container relative max-w-full',
      computedClass,
      !avatarImage || imageLoadError ? 'border border-theme-divider bg-grey-200 dark:bg-grey-700' : '',
    ]"
  >
    <img
      v-if="avatarImage && !imageLoadError"
      class="object-cover w-full h-full"
      :src="avatarImage"
      :alt="userData?.fullname"
      loading="lazy"
      @error="imageLoadError = true"
    />
    <div
      v-else
      data-testid="avatar-initials"
      :class="['text-secondary dark:text-grey-300 font-semibold leading-none select-none', textSize]"
    >
      {{ getInitials() }}
    </div>
  </div>
  <div
    v-if="showStatus"
    class="w-2.5 h-2.5 z-100 rounded-full border border-theme-paper -bottom-0.5 -right-0.5 absolute"
    :class="{
      'bg-success': status === 'online',
      'bg-grey-400 dark:bg-grey-600': status !== 'online',
    }"
  >
    <div class="w-full h-full border-2 border-transparent rounded-full"></div>
  </div>
</template>
<script>
export default {
  props: {
    class: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 8,
    },
    user: {
      type: Object,
      default: null,
    },
    showStatus: {
      type: Boolean,
      default: false,
    },
    has_shadow: {
      type: Boolean,
      default: false,
    },

    postOrganization: {
      type: Object,
      default: null,
      required: false,
    },

    countryAvatar: {
      type: String,
      default: null,
      required: false,
    },
    useOptimizedAvatar: {
      type: Boolean,
      default: true,
    },
    preview: {
      type: Boolean,
      default: true,
    },
    isFullPath: {
      type: Boolean,
      default: false,
    },
    initialsOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      path: import.meta.env.VITE_APP_URL,
      status: 'offline',
      observer: null,
      imageLoadError: false,
    };
  },
  computed: {
    computedClass() {
      return [
        this.has_shadow ? 'shadow' : '',
        `w-${this.size}`,
        `h-${this.size}`,
        'bg-theme-paper',
        this.class,
      ].filter(Boolean).join(' ');
    },
    textSize() {
      // Tailwind sizes are 0.25rem steps, so the circle is `size * 4` px wide.
      // The initials sit at roughly 40% of that, matching the avatar scale.
      const diameter = Number(this.size) * 4;

      if (diameter >= 128) return 'text-5xl';
      if (diameter >= 96) return 'text-4xl';
      if (diameter >= 64) return 'text-2xl';
      if (diameter >= 56) return 'text-xl';
      if (diameter >= 48) return 'text-lg';
      if (diameter >= 40) return 'text-base';
      if (diameter >= 36) return 'text-sm';

      return 'text-xs';
    },
    avatarImage() {
      if (this.initialsOnly) return '';
      if (this.countryAvatar !== null) {
        return this.path + this.countryAvatar;
      }

      // Check user's own avatar first (takes priority over organization logo)
      if (this.preview && this.userData?.profile_avatar?.preview) {
        return this.path + this.userData?.profile_avatar?.preview;
      }

      if (this.userData && (this.userData.avatar || this.userData.profile_avatar)) {
        if (this.useOptimizedAvatar && this.userData?.profile_avatar?.url_webp) {
          return this.path + this.userData?.profile_avatar?.url_webp;
        } else if (this.isFullPath) {
          return this.userData.avatar;
        }
        return this.path + (this.userData?.profile_avatar?.url || this.userData.avatar);
      }

      // Fallback to organization logo if user has no avatar
      if (this.postOrganization && this.postOrganization.logo) {
        return this.path + this.postOrganization.logo;
      }

      // Alex avatar
      if (this.userData?.id == 0) {
        return this.path + '/images/agent-alex.png';
      }

      return '';
    },
    userData() {
      return this.user || this.$page.props.auth.user;
    },
  },
  watch: {
    avatarImage() {
      this.imageLoadError = false;
    },
  },
  methods: {
    getInitials() {
      const firstname = this.userData?.firstname;
      const lastname = this.userData?.lastname;
      if (!firstname && !lastname) return '';
      //check if not exist lastname
      if (!lastname) return firstname[0].toUpperCase();
      if (!firstname) return lastname[0].toUpperCase();
      return `${firstname[0]}${lastname[0]}`.toUpperCase();
    },
    getUserStatus() {
      axios.get(route('chat.user.get.status', this.user.id)).then((response) => {
        this.status = response.data.status;
      });
    },
  },
  mounted() {
    if (this.showStatus && this.$refs.avatarRoot && typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            this.getUserStatus();
            this.observer.unobserve(this.$refs.avatarRoot);
          }
        },
        {
          rootMargin: '0px',
          threshold: 0.1,
        }
      );
      this.observer.observe(this.$refs.avatarRoot);
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>
<style>
.circle-container {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
}

.circle-container img {
  border-radius: 50%;
}
</style>
